import React, { createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isTokenExpired } from '../utils/tokenManagement';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  /**
   * Handle authentication failure (401/403)
   * Signs out the user and redirects to login
   */
  const handleAuthFailure = useCallback((message = 'Session expired. Please sign in again.') => {
    try {
      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('account');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      
      // Only show toast if not already on signin page
      const currentPath = window.location.pathname;
      if (currentPath !== '/signin' && currentPath !== '/signup') {
        // Dismiss all other toasts to prevent duplicates
        toast.dismiss();
        
        // Show notification to user with proper styling
        toast.error(message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          limit: 1, // Only show one auth error at a time
          style: {
            fontSize: '14px',
            padding: '12px',
          },
        });
      }

      // Redirect to signin page
      navigate('/signin', { replace: true });
    } catch (error) {
      console.error('Error handling auth failure:', error);
    }
  }, [navigate]);

  /**
   * Check if user has a valid token
   */
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (!token || !user) {
      return false;
    }

    if (isTokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('account');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      return false;
    }

    return true;
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('account');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    navigate('/signin', { replace: true });
  }, [navigate]);

  const value = {
    handleAuthFailure,
    isAuthenticated,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
